import { useState } from 'react';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTheme } from 'hooks';
import { Logo } from 'assets';
import { LandscapeLock } from 'components/containers';
import { Button } from 'components/partials';
import {
  selectLocalLogoUrl,
  selectSelectedWorkflow,
  selectPublicUserTrackingInfo,
  getPhotoSeries
} from 'redux/root.selectors';
import {
  setPublicUserTrackingInfo
} from 'redux/root.actions';
import { IWorkflow } from 'redux/workflows/workflows';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { makePatch, useTranslate } from 'services';

import { IPhotoSeries } from 'redux/photoseries/photoseries';

const { photoseries, vehicle_scan } = TRANSLATIONS_VALUES_KEYS;

const LicensePlatePageWrapper = styled.div`
  display: flex;

  & > div {
    width: 50%;
  }
`;

const LicensePlatePageWrapperCol1 = styled.div<{ isLocalLogo: boolean, background: string }>`
  background: ${(props) => props.background};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: ${(props) => (props.isLocalLogo ? '0' : '1rem')};

  & a {
    width: 100%;

    & img {
      margin: 0 auto;
      display: flex;
      justify-content: center;
    }
  }
`;

const LicensePlatePageWrapperCol2 = styled.div<{ background: string }>`
  background: ${(props) => props.background};
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 3rem 3rem 2.5rem 3rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Title = styled.h2`
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const SubTitle = styled.h3`
  font-weight: 500;
  font-size: 0.8125rem;
  margin-bottom: 1rem;
`;

const LicensePlateRow = styled.div`
  font-size: 1rem;
  display: flex;
  font-size: 0.8125rem;
  padding-bottom: 1rem;
  flex-direction: column;
  justify-content: end;

  h2 {
    font-size: 0.813rem;
    text-align: center;
  }
`;

const LicensePlateInput = styled.input<{ color: string, background: string, border: string }>`
  background: ${({ background }) => background};
  border: 1px solid ${({ border }) => border};
  height: 3.375rem;
  border: 1px solid;
  border-radius: 0.625rem;
  text-align: center;
  text-transform: uppercase;
  font-size: 1.625rem !important;
  font-weight: 700;
  color: ${({ color }) => color};

  &:focus {
    outline: none;
  }
`;

function LicensePlate() {
  const [licensePlate, setLicensePlate] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const history = useHistory();
  const localLogo: string = useSelector(selectLocalLogoUrl);
  const {
    styles: {
      colorParagraph,
      colorBackgroundSurface,
      colorBackgroundRaised,
      colorBackgroundGradient,
      colorBackgroundBase
    },
  } = useTheme();
  const dispatch = useDispatch();

  const publicUserTrackingInfo = useSelector(selectPublicUserTrackingInfo);
  const selectedWorkflow: IWorkflow = useSelector(selectSelectedWorkflow);
  const photoSeries: IPhotoSeries = useSelector(getPhotoSeries);
  const { translate } = useTranslate();

  const onClickHandler = () => {
    setIsRedirecting(true);

    makePatch(`capture/${photoSeries.id}/registration-number`, {
      registrationNo: licensePlate
    }).subscribe(() => {
      // updating tracking information
      const newPublicUserTrackingInfo = {
        ...publicUserTrackingInfo,
      };

      newPublicUserTrackingInfo.photoSeries.vehicle.registrationNo = licensePlate;
      dispatch(setPublicUserTrackingInfo(newPublicUserTrackingInfo));

      history.push(`/capture/vehicle-type/${selectedWorkflow.vehicleType}`);
    });
  };

  return (
    <LandscapeLock>
      <LicensePlatePageWrapper className="wrapper">
        <LicensePlatePageWrapperCol1 isLocalLogo={!!localLogo} background={colorBackgroundGradient}>
          <Logo />
        </LicensePlatePageWrapperCol1>

        <LicensePlatePageWrapperCol2 background={colorBackgroundBase}>
          <Title data-testid="landing-title">
            { translate(photoseries.welcome_heading_text) }
          </Title>
          <SubTitle>
            { translate(photoseries.license_plate_subheading_text) }
          </SubTitle>
          <LicensePlateRow>
            <h2>
              { translate(photoseries.insert_license_plate_text) }
            </h2>
            <LicensePlateInput
              type="text"
              spellCheck="false"
              onChange={(e) => {
                setLicensePlate(e.target.value.trim());
              }}
              value={licensePlate}
              disabled={isRedirecting}
              background={colorBackgroundSurface}
              border={colorBackgroundRaised}
              color={colorParagraph}
            />
          </LicensePlateRow>
          <Button
            testId="continuePSCaptureBtn"
            onClick={onClickHandler}
            disabled={licensePlate.length < 2 || isRedirecting}
            animate
          >
            { translate(vehicle_scan.continue_btn) }
          </Button>
        </LicensePlatePageWrapperCol2>
      </LicensePlatePageWrapper>
    </LandscapeLock>
  );
}

export default LicensePlate;
